import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Bell, Save, Send, Mail, MessageSquare, AlertTriangle } from "lucide-react";

// Provider option lists mirror backend/notif_config.py.
const EMAIL_PROVIDERS = [
  { value: "console", label: "Console (log only — no send)" },
  { value: "sendgrid", label: "SendGrid" },
  { value: "resend", label: "Resend" },
  { value: "smtp", label: "Custom SMTP server" },
];
const SMTP_SECURITY = [
  { value: "starttls", label: "STARTTLS (recommended, port 587)" },
  { value: "ssl", label: "SSL/TLS (port 465)" },
  { value: "none", label: "None — plaintext (localhost relay only)" },
];
const SMS_PROVIDERS = [
  { value: "console", label: "Console (log only — no send)" },
  { value: "twilio", label: "Twilio" },
  { value: "africastalking", label: "Africa's Talking" },
];

const blank = () => ({
  email_provider: "console", email_enabled: true, email_from_email: "", email_from_name: "", email_api_key: "",
  email_smtp_host: "", email_smtp_port: "", email_smtp_username: "", email_smtp_password: "", email_smtp_security: "starttls",
  sms_provider: "console", sms_enabled: false, sms_from_number: "",
  sms_account_sid: "", sms_auth_token: "", sms_username: "", sms_api_key: "",
});

export default function NotificationsSettings() {
  const [f, setF] = useState(blank());
  const [loaded, setLoaded] = useState(false);
  const [secretsOk, setSecretsOk] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState({ email: "", sms: "" });
  const [testing, setTesting] = useState({ email: false, sms: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k) => (e) => setF({ ...f, [k]: e.target.checked });

  useEffect(() => {
    api.get("/v1/admin/settings/notifications")
      .then(({ data }) => {
        setSecretsOk(data.secrets_configured !== false);
        const d = data.data || {};
        const em = d.email || {}, sm = d.sms || {};
        setF({
          email_provider: em.provider || "console",
          email_enabled: em.enabled !== undefined ? em.enabled : true,
          email_from_email: em.from_email || "",
          email_from_name: em.from_name || "",
          email_api_key: em.config?.api_key || "",   // "••••••" if already set
          email_smtp_host: em.config?.host || "",
          email_smtp_port: em.config?.port || "",
          email_smtp_username: em.config?.username || "",
          email_smtp_password: em.config?.password || "",   // "••••••" if already set
          email_smtp_security: em.config?.security || "starttls",
          sms_provider: sm.provider || "console",
          sms_enabled: sm.enabled !== undefined ? sm.enabled : false,
          sms_from_number: sm.from_number || "",
          sms_account_sid: sm.config?.account_sid || "",
          sms_auth_token: sm.config?.auth_token || "",
          sms_username: sm.config?.username || "",
          sms_api_key: sm.config?.api_key || "",
        });
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load notification settings"))
      .finally(() => setLoaded(true));
  }, []);

  const emailConfig = () =>
    f.email_provider === "smtp"
      ? { host: f.email_smtp_host, port: f.email_smtp_port, username: f.email_smtp_username,
          password: f.email_smtp_password, security: f.email_smtp_security }
      : { api_key: f.email_api_key };

  const buildPayload = () => ({
    data: {
      email: {
        provider: f.email_provider, enabled: f.email_enabled,
        from_email: f.email_from_email, from_name: f.email_from_name,
        config: emailConfig(),
      },
      sms: {
        provider: f.sms_provider, enabled: f.sms_enabled,
        from_number: f.sms_from_number,
        config: { account_sid: f.sms_account_sid, auth_token: f.sms_auth_token,
                  username: f.sms_username, api_key: f.sms_api_key },
      },
    },
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/v1/admin/settings/notifications", buildPayload());
      toast.success("Notification settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendTest = async (channel) => {
    const to = testTo[channel]?.trim();
    if (!to) { toast.error(`Enter a ${channel === "email" ? "recipient email" : "phone number"} first`); return; }
    setTesting((t) => ({ ...t, [channel]: true }));
    try {
      const { data } = await api.post("/v1/admin/notifications/test", { channel, to });
      const r = data.result || {};
      if (r.status === "ok") toast.success(`Test ${channel} sent via ${r.provider}`);
      else if (r.status === "skipped") toast.warning(r.reason || `No ${channel} provider configured`);
      else toast.error(`Test failed: ${r.error || r.status || "unknown error"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting((t) => ({ ...t, [channel]: false })); }
  };

  const emailReal = f.email_provider !== "console";
  const isSmtp = f.email_provider === "smtp";

  return (
    <PortalLayout section="admin">
      <div className="vtx-eyebrow">/ Admin · Settings</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter inline-flex items-center gap-2"><Bell size={24} /> Notifications</h1>
      <p className="text-sm text-[#475569] mt-1">Choose how shipment updates go out. Secret keys are stored encrypted and never shown again — leave a key blank to keep the saved one.</p>

      {loaded && !secretsOk && (
        <div className="mt-5 vtx-card p-4 bg-[#FFF7ED] border-[#F0A63A]/50 flex gap-3" data-testid="secrets-warning">
          <AlertTriangle size={18} className="text-[#B45309] shrink-0 mt-0.5" />
          <div className="text-sm text-[#7c4a12] leading-relaxed">
            <span className="font-bold">Secret storage isn't configured.</span> API keys and the SMTP password
            can't be saved until you set <code className="font-mono text-xs bg-white/70 px-1 py-0.5 rounded">SETTINGS_ENC_KEY</code> on
            the server and restart. Generate one with:
            <code className="block font-mono text-[11px] bg-white/70 mt-1.5 px-2 py-1 rounded overflow-x-auto">python -c "from cryptography.fernet import Fernet; print(Fernet.generate_key().decode())"</code>
            You can still save console/no-auth options without it.
          </div>
        </div>
      )}

      {!loaded ? (
        <div className="vtx-card mt-6 p-6"><div className="vtx-skeleton h-40" /></div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Email */}
          <div className="vtx-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="vtx-eyebrow inline-flex items-center gap-1.5"><Mail size={13} /> Email</div>
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#0E2F76]">
                <input type="checkbox" checked={f.email_enabled} onChange={toggle("email_enabled")} data-testid="email-enabled" /> Enabled
              </label>
            </div>
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Provider</span>
              <select className="vtx-input" value={f.email_provider} onChange={set("email_provider")} data-testid="email-provider">
                {EMAIL_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>
            <div className="grid md:grid-cols-2 gap-3">
              <label><span className="vtx-eyebrow block mb-1.5">From email</span><input className="vtx-input" value={f.email_from_email} onChange={set("email_from_email")} placeholder="no-reply@yourco.com" data-testid="email-from" /></label>
              <label><span className="vtx-eyebrow block mb-1.5">From name</span><input className="vtx-input" value={f.email_from_name} onChange={set("email_from_name")} placeholder="Ventrix Freights" /></label>
            </div>
            {emailReal && !isSmtp && (
              <label className="block"><span className="vtx-eyebrow block mb-1.5">API key (encrypted)</span><input className="vtx-input font-mono" value={f.email_api_key} onChange={set("email_api_key")} placeholder="leave blank to keep" data-testid="email-api-key" /></label>
            )}
            {isSmtp && (
              <div className="space-y-3">
                <div className="grid md:grid-cols-3 gap-3">
                  <label className="md:col-span-2"><span className="vtx-eyebrow block mb-1.5">SMTP host</span><input className="vtx-input font-mono" value={f.email_smtp_host} onChange={set("email_smtp_host")} placeholder="smtp.yourco.com" data-testid="smtp-host" /></label>
                  <label><span className="vtx-eyebrow block mb-1.5">Port</span><input className="vtx-input font-mono" value={f.email_smtp_port} onChange={set("email_smtp_port")} placeholder="587" data-testid="smtp-port" /></label>
                </div>
                <label className="block"><span className="vtx-eyebrow block mb-1.5">Security</span>
                  <select className="vtx-input" value={f.email_smtp_security} onChange={set("email_smtp_security")} data-testid="smtp-security">
                    {SMTP_SECURITY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <div className="grid md:grid-cols-2 gap-3">
                  <label><span className="vtx-eyebrow block mb-1.5">Username</span><input className="vtx-input font-mono" value={f.email_smtp_username} onChange={set("email_smtp_username")} placeholder="smtp user (optional)" data-testid="smtp-username" /></label>
                  <label><span className="vtx-eyebrow block mb-1.5">Password (encrypted)</span><input className="vtx-input font-mono" type="password" value={f.email_smtp_password} onChange={set("email_smtp_password")} placeholder="leave blank to keep" data-testid="smtp-password" /></label>
                </div>
              </div>
            )}
            <div className="border-t border-[#A9C0E0]/40 pt-3">
              <div className="vtx-eyebrow mb-1.5">Send a test</div>
              <div className="flex gap-2">
                <input className="vtx-input flex-1" value={testTo.email} onChange={(e) => setTestTo((t) => ({ ...t, email: e.target.value }))} placeholder="you@yourco.com" data-testid="email-test-to" />
                <button className="vtx-btn-ghost inline-flex items-center gap-1.5 whitespace-nowrap" disabled={testing.email} onClick={() => sendTest("email")} data-testid="email-test-send"><Send size={13} /> Test</button>
              </div>
              <p className="text-[11px] text-[#475569] mt-1.5">Uses the saved provider. Save first if you just changed the key.</p>
            </div>
          </div>

          {/* SMS */}
          <div className="vtx-card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="vtx-eyebrow inline-flex items-center gap-1.5"><MessageSquare size={13} /> SMS</div>
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#0E2F76]">
                <input type="checkbox" checked={f.sms_enabled} onChange={toggle("sms_enabled")} data-testid="sms-enabled" /> Enabled
              </label>
            </div>
            <label className="block"><span className="vtx-eyebrow block mb-1.5">Provider</span>
              <select className="vtx-input" value={f.sms_provider} onChange={set("sms_provider")} data-testid="sms-provider">
                {SMS_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>
            <label className="block"><span className="vtx-eyebrow block mb-1.5">From number / sender ID</span><input className="vtx-input font-mono" value={f.sms_from_number} onChange={set("sms_from_number")} placeholder="+254700000000 / VENTRIX" data-testid="sms-from" /></label>
            {f.sms_provider === "twilio" && (
              <div className="grid md:grid-cols-2 gap-3">
                <label><span className="vtx-eyebrow block mb-1.5">Account SID</span><input className="vtx-input font-mono" value={f.sms_account_sid} onChange={set("sms_account_sid")} placeholder="AC…" data-testid="sms-account-sid" /></label>
                <label><span className="vtx-eyebrow block mb-1.5">Auth token (encrypted)</span><input className="vtx-input font-mono" value={f.sms_auth_token} onChange={set("sms_auth_token")} placeholder="leave blank to keep" data-testid="sms-auth-token" /></label>
              </div>
            )}
            {f.sms_provider === "africastalking" && (
              <div className="grid md:grid-cols-2 gap-3">
                <label><span className="vtx-eyebrow block mb-1.5">Username</span><input className="vtx-input font-mono" value={f.sms_username} onChange={set("sms_username")} placeholder="sandbox / your username" data-testid="sms-username" /></label>
                <label><span className="vtx-eyebrow block mb-1.5">API key (encrypted)</span><input className="vtx-input font-mono" value={f.sms_api_key} onChange={set("sms_api_key")} placeholder="leave blank to keep" data-testid="sms-api-key" /></label>
              </div>
            )}
            <div className="border-t border-[#A9C0E0]/40 pt-3">
              <div className="vtx-eyebrow mb-1.5">Send a test</div>
              <div className="flex gap-2">
                <input className="vtx-input flex-1 font-mono" value={testTo.sms} onChange={(e) => setTestTo((t) => ({ ...t, sms: e.target.value }))} placeholder="+254700000000" data-testid="sms-test-to" />
                <button className="vtx-btn-ghost inline-flex items-center gap-1.5 whitespace-nowrap" disabled={testing.sms} onClick={() => sendTest("sms")} data-testid="sms-test-send"><Send size={13} /> Test</button>
              </div>
              <p className="text-[11px] text-[#475569] mt-1.5">Customer accounts have no phone on file, so shipment SMS relies on this test path for now.</p>
            </div>
          </div>

          <div className="lg:col-span-2 flex justify-end">
            <button className="vtx-btn-primary inline-flex items-center gap-2" disabled={busy} onClick={save} data-testid="save-notifications"><Save size={14} /> Save notification settings</button>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
